import { forEach, isArray, isEmpty, merge } from 'lodash';
import shortId from 'shortid';
import { on, once, remove } from '../event';
import type { IController } from './Controller.interface';
import Data from './Data';
import Event from './Event';
import type { IEvent } from './Event.interface';
import Scope from './Scope';
import Storage from './Storage';

export default class Controller implements IController {
  Storage: Storage;
  Scope: Scope;
  Data: Data;

  eventsSubscribe = (events: Array<IEvent>, unique: Boolean = true) =>
    forEach(events, (e) => {
      const event = new Event(e);
      if (unique) {
        remove(event.name);
      }
      if (event.handler) {
        if (event.once) {
          once(event.name, this.getHandler(event.handler), event.context);
        } else {
          on(event.name, this.getHandler(event.handler), event.context);
        }
      }
    });

  eventsUnsubscribe = (events: Array<IEvent>) =>
    forEach(events, (e) => {
      const event = new Event(e);
      remove(event.name);
    });

  getDataCollectionHasMore = () => this.Data?.getCollectionHasMore();
  getDataCollectionIsSelected = (id: String, collection?: Array<Object>) =>
    this.Data?.getCollectionIsSelected(id, collection);

  getDataCollectionPage = () => this.Data?.getCollectionPage();
  getDataCollectionPageCount = () => this.Data?.getCollectionPageCount();
  getDataCollectionPageCurrent = () => this.Data?.getCollectionPageCurrent();
  getDataCollectionPageOffset = () => this.Data?.getCollectionPageOffset();
  getDataCollectionSelection = () => this.Data?.getCollectionSelection();
  getDataCollectionSize = () => this.Data?.getCollectionSize();
  getDataCollectionData = () => this.Data?.getCollectionData();
  getDataCollectionDataLength = () => this.Data?.getCollectionDataLength();
  getDataCollectionDataIds = (uniqId) => this.Data?.getCollectionDataIds(uniqId);
  getDataLimit = () => this.Data.Limit;
  getDataTotal = () => this.Data.Total;
  getDataWillBeTaxing = () => this.Data.getWillBeTaxing();

  getHandler =
    (fn: Function) =>
    (e: Object, ...args: any) => {
      if (e && e.stopPropagation) {
        e.stopPropagation();
      }
      return fn(...args);
    };

  resetDataAndScope = (
    request: String = 'available',
    requestParameters: Object = {},
    scope: Object = {},
    isUpdated: Boolean = true
  ) => {
    if (this.Data && this.Scope) {
      this.Data.reset(request, requestParameters, isUpdated);
      this.Scope.set(scope, isUpdated);
    }
  };

  setDataCollectionSelection = (id: String) => this.Data?.setCollectionSelection(id);

  init = async (events, dependencies, dependenciesForAngular, dependenciesRunInState) => {
    const runnableState = this.Scope.State.current.name === dependenciesRunInState;

    //
    // QUERIES MUST BE RESET BEFORE LOADING ~ All data will be reset within Controller if this runs later
    //
    const filter = await this.Storage.get('Filter');
    const startingWith = await this.Storage.get('StartingWith');
    const orderCoerceAndDirection = await this.Storage.get('OrderBy');
    this.Data.reset('available', {
      query: merge(
        {},
        filter,
        { startingWith: startingWith !== 'ALL' ? startingWith : '' },
        orderCoerceAndDirection
      )
    });

    //
    // DEPENDENCIES CAN NOW RUN ~ Data can be loaded since we prepared Controller
    //
    if (runnableState) {
      if (isArray(events) && !isEmpty(events)) {
        this.eventsSubscribe(events);
      }

      if (isArray(dependencies) && !isEmpty(dependencies)) {
        /* eslint-disable no-restricted-syntax */
        /* eslint-disable no-await-in-loop */
        /* eslint-disable no-unused-vars */
        for (const Dependency of dependencies) {
          await new Dependency(...dependenciesForAngular);
        }
        /* eslint-enable no-restricted-syntax */
        /* eslint-enable no-await-in-loop */
        /* eslint-enable no-unused-vars */
      }
    }

    return runnableState;
  };

  /**
   * Constructor
   * @param {Array<Object>} angularDependencies
   * @param {IController} data
   */
  constructor(angularDependencies?: Array<Object>, data?: IController) {
    const [$rootScope, $scope, $state, $timeout, webpage, eventService, authenticationService] =
      angularDependencies;
    const idCurrentOrganization = btoa($rootScope.CurrentOrganization.id);
    const idCurrentAccount = btoa($rootScope.CurrentAccount.id);

    const defaults = {
      Storage: {
        DataBase: 'st',
        Keys: {
          Filter: `F:${idCurrentOrganization}${idCurrentAccount}`,
          StartingWith: `W:${idCurrentOrganization}${idCurrentAccount}`,
          OrderBy: `O:${idCurrentOrganization}${idCurrentAccount}`
        }
      },
      Scope: {
        Root: $rootScope,
        Current: $scope,
        State: $state,
        TimeOut: $timeout,
        WebPage: webpage,
        Event: eventService,
        Auth: authenticationService,
        Defaults: {
          ComponentUpdated: shortId.generate(),
          isReady: { Controller: false }
        }
      },
      Data: {
        Model: undefined,
        Requests: {
          available: {
            url: '/api/v1/',
            method: 'GET',
            header: {
              Authorization: `Bearer ${authenticationService.tokenGet()}`
            },
            query: {
              isActive: true
            }
          }
        },
        useCaching: false
      }
    };

    this.Storage = new Storage(merge(defaults.Storage, data?.Storage));
    this.Scope = new Scope(merge(defaults.Scope, data?.Scope));
    this.Data = new Data(merge(defaults.Data, data?.Data));

    this.Scope.set({
      Data: { [data?.Scope?.Defaults?.Name || this?.constructor?.name]: this.Data }
    });
  }
}
